import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from '../../../core/api.service';
import { SalesOrder } from '../models/sales-order';

@Injectable({
  providedIn: 'root'
})
export class SalesOrderService {
  constructor(
    private readonly http: HttpClient,
    private readonly apiService: ApiService
  ) {}

  searchByLocationAndDateRange(
    locationId: string,
    startDate: Date,
    endDate: Date,
    numberOfRecords: number,
    showVoidedOrders: boolean
  ): Observable<SalesOrder[]> {
    const params = new HttpParams()
      .set('locationId', locationId)
      .set('startDate', startDate.toISOString())
      .set('endDate', endDate.toISOString())
      .set('numberOfRecords', String(numberOfRecords))
      .set('showVoidedOrders', String(showVoidedOrders));

    return this.http.get<SalesOrder[]>(this.apiService.buildUrl('api/salesorder/searchByLocationAndDateRange'), {
      params
    });
  }

  search(searchValue: string): Observable<SalesOrder[]> {
    const params = new HttpParams().set('searchValue', searchValue);
    return this.http
      .get<ApiSalesOrderFullDto[]>(this.apiService.buildUrl('api/salesorder/search'), { params })
      .pipe(map((results) =>
        (results ?? []).map((r) => ({
          id: String(r.id ?? ''),
          number: r.number ?? '',
          referenceNumber: r.referenceNumber ?? undefined,
          status: r.status ?? 0,
          date: r.date ?? undefined,
          locationName: r.locationName ?? undefined,
          serviceProviderName: r.serviceProviderName ?? undefined
        } satisfies SalesOrder))
      ));
  }

  getFullDetail(soNumber: string): Observable<SalesOrderFullDetail | null> {
    const params = new HttpParams().set('searchValue', soNumber);
    return this.http
      .get<ApiSalesOrderFullDto[]>(this.apiService.buildUrl('api/salesorder/search'), { params })
      .pipe(map((results) => {
        const match = (results ?? []).find((r) => r.number === soNumber);
        if (!match) return null;
        return {
          id: String(match.id ?? ''),
          number: match.number ?? '',
          referenceNumber: match.referenceNumber ?? undefined,
          status: match.status ?? 0,
          date: match.date ?? undefined,
          locationName: match.locationName ?? undefined,
          locationStreet: match.locationStreet ?? undefined,
          locationCity: match.locationCity ?? undefined,
          locationState: match.locationState ?? undefined,
          locationZip: match.locationZip ?? undefined,
          serviceProviderName: match.serviceProviderName ?? undefined,
          shipVia: match.shipVia ?? undefined,
          shipViaType: match.shipViaType ?? undefined,
          callerFirstName: match.callerFirstName ?? undefined,
          callerLastName: match.callerLastName ?? undefined,
          callerPhoneNumber: match.callerPhoneNumber ?? undefined,
          specialInstructions: match.specialInstructions ?? undefined,
          createdOn: match.createdOn ?? undefined,
          modifiedOn: match.modifiedOn ?? undefined,
          isElectronicPODPresent: match.isElectronicPODPresent ?? false,
          lineItems: (match.salesOrderDetails ?? []).map((d) => ({
            id: d.id ?? undefined,
            productId: d.productId ?? undefined,
            salesPricingId: d.salesPricingId ?? undefined,
            productName: d.productName ?? '',
            productDescription: d.productDescription ?? undefined,
            quantity: d.quantity ?? 0,
            poNumber: d.poNumber || d.poNumber2
          }))
        } satisfies SalesOrderFullDetail;
      }));
  }

  update(request: SalesOrderUpdateRequest): Observable<unknown> {
    return this.http.put(this.apiService.buildUrl('api/salesorder'), request);
  }

  voidSalesOrder(salesOrderId: string): Observable<void> {
    return this.http.put<void>(this.apiService.buildUrl(`api/salesorder/void/${salesOrderId}`), {});
  }

  downloadPod(salesOrderId: string): Observable<Blob> {
    return this.http.get(this.apiService.buildUrl(`api/report/pod/${salesOrderId}`), {
      responseType: 'blob'
    });
  }

  create(request: SalesOrderCreateRequest): Observable<string> {
    return this.http.post<string>(this.apiService.buildUrl('api/salesorder/createSalesOrderRequest'), request);
  }

  searchReport(request: SalesOrderReportSearchRequest): Observable<SalesOrder[]> {
    return this.http
      .post<ApiSalesOrderReportRow[]>(this.apiService.buildUrl('api/report/salesorder/search'), request)
      .pipe(
        map((rows) =>
          (rows ?? []).map((row) => ({
            id: row.invoiceId ?? `${row.salesOrderNumber ?? ''}-${row.customerPo ?? ''}-${row.shipDateTime ?? ''}`,
            number: row.salesOrderNumber ?? '',
            referenceNumber: row.bolNumber ?? undefined,
            status: row.salesOrderStatus ?? 0,
            date: row.shipDateTime ?? undefined,
            locationName: row.locationName ?? undefined,
            serviceProviderName: undefined
          } satisfies SalesOrder))
        )
      );
  }
}

export interface SalesOrderUpdateRequest {
  id: string;
  requestedDate?: string;
  contactFirstName?: string;
  contactLastName?: string;
  contactPhoneNumber?: string;
  specialInstructions?: string;
  shipVia?: string;
  shipViaType?: number;
  accountId?: string;
  serviceProviderId?: string;
  updatedDetails?: ApiSalesOrderDetail[];
}

export interface ApiSalesOrderDetail {
  id?: string;
  productId?: string;
  productName?: string;
  productDescription?: string;
  salesPricingId?: string;
  quantity: number;
  poNumber?: string;
  poNumber2?: string;
  poId?: string;
  poLine?: string;
}

export interface SalesOrderCreateRequest {
  locationId: string;
  serviceProviderId: string;
  requestedDate: string;
  contactFirstName: string;
  contactLastName: string;
  contactPhoneNumber: string;
  detailList: SalesOrderCreateRequestDetail[];
  specialInstructions: string;
  shipVia: string;
  shipViaType: number;
}

export interface SalesOrderCreateRequestDetail {
  productId: string;
  quantity: number;
  poNumber2: string;
  salesPricingId: string;
}

interface SalesOrderReportSearchRequest {
  startDate?: string;
  endDate?: string;
  purchaseOrderFilter?: string;
  locationIds?: string[];
  productIds?: string[];
  salesOrderStatuses?: string[];
}

interface ApiSalesOrderReportRow {
  locationName?: string;
  salesOrderNumber?: string;
  salesOrderStatus?: number;
  shipDateTime?: string;
  customerPo?: string;
  bolNumber?: string;
  invoiceId?: string;
}

export interface SalesOrderFullDetail {
  id: string;
  number: string;
  referenceNumber?: string;
  status: number;
  date?: string;
  locationName?: string;
  locationStreet?: string;
  locationCity?: string;
  locationState?: string;
  locationZip?: string;
  serviceProviderName?: string;
  shipVia?: string;
  shipViaType?: number;
  callerFirstName?: string;
  callerLastName?: string;
  callerPhoneNumber?: string;
  specialInstructions?: string;
  createdOn?: string;
  modifiedOn?: string;
  isElectronicPODPresent: boolean;
  lineItems: SalesOrderLineItem[];
}

export interface SalesOrderLineItem {
  id?: string;
  productId?: string;
  salesPricingId?: string;
  productName: string;
  productDescription?: string;
  quantity: number;
  poNumber?: string | null;
}

interface ApiSalesOrderFullDto {
  id?: string;
  number?: string;
  referenceNumber?: string;
  status?: number;
  date?: string;
  locationName?: string;
  locationStreet?: string;
  locationCity?: string;
  locationState?: string;
  locationZip?: string;
  serviceProviderName?: string;
  shipVia?: string;
  shipViaType?: number;
  callerFirstName?: string;
  callerLastName?: string;
  callerPhoneNumber?: string;
  specialInstructions?: string;
  createdOn?: string;
  modifiedOn?: string;
  isElectronicPODPresent?: boolean;
  salesOrderDetails?: ApiSalesOrderDetailDto[];
}

interface ApiSalesOrderDetailDto {
  id?: string;
  productId?: string;
  salesPricingId?: string;
  productName?: string;
  productDescription?: string;
  quantity?: number;
  poNumber?: string;
  poNumber2?: string;
  poId?: string;
}
